import type { Company } from '@shared/types/index.ts';

/**
 * Settings sections that open on a page of their own instead of as a card on
 * /settings, while the deployment's FEATURE_SETTINGS_HUB_ROUTES flag is on.
 * Sections that are pages unconditionally (Pricing & quotes) are listed in
 * settings-nav.tsx's SETTINGS_SECTION_ROUTES instead.
 *
 * Keyed by the nav section id (the same id the card's `settings-<id>` anchor
 * used), so the rail, the Essentials tiles and the setup guide all agree on
 * where an entry goes. The pages themselves are always routed — a link minted
 * to one keeps working while the flag is off — and each still applies the
 * permission and plan checks its card had on /settings.
 */
export const SETTINGS_HUB_SECTION_ROUTES: Readonly<Record<string, string>> = {
  library: '/settings/templates',
  team: '/settings/team',
  teamPay: '/settings/team-pay',
  account: '/settings/account',
  security: '/settings/security',
};

/**
 * Old in-page fragments, and the page each now opens. Emails, bookmarks and
 * the OAuth returns minted `/settings#settings-<id>` for years; while the
 * flag is on, /settings forwards those (query string kept) instead of
 * scrolling to a card that is no longer there.
 */
export const SETTINGS_HUB_HASH_ROUTES: Readonly<Record<string, string>> = {
  '#settings-library': '/settings/templates',
  '#settings-roomTypes': '/settings/templates/rooms',
  '#settings-layouts': '/settings/templates/groups',
  '#settings-team': '/settings/team',
  '#settings-teamPay': '/settings/team-pay',
  '#settings-account': '/settings/account',
  '#settings-security': '/settings/security',
  // The one fragment outside the `#settings-<id>` scheme: the mailbox and
  // calendar OAuth legs append it to their return, outcome in the query.
  '#integrations': '/settings/integrations',
};

/** Whether this company sees Settings child pages (FEATURE_SETTINGS_HUB_ROUTES). */
export function settingsHubRoutesOn(company: Pick<Company, 'settingsHubRoutes'> | null | undefined): boolean {
  return company?.settingsHubRoutes === true;
}
